import React, { useEffect, useState } from 'react'
import CsvDownloadButton from 'react-json-to-csv'
import './eval-results-display.styles.scss'

const EvalResultsDisplay = ({ questionSets, userEvaluations, evaluationDataList }) => {

  const [displayData, setDisplayData] = useState(null);
  const [csvData, setCsvData] = useState([]);

  useEffect(() => {
    if (!questionSets || !userEvaluations) return;
    compileTemplate();
  }, [questionSets, userEvaluations])

  function compileTemplate() {
    const evalList = Object.values(userEvaluations);

    const compiled = questionSets.map(set => {
      const questions = Object.values(set.questions || {})
        .filter(q => q.type !== 'info')
        .sort((a, b) => a.order - b.order)
        .map(q => {
          if (q.type === 'multiple_choice' || q.type === 'checkbox') {
            const options = (q.options || []).map(opt => ({
              id: opt.id,
              text: opt.text,
              count: evalList.filter(ev =>
                ev[opt.id] === 1 || ev[q.id] === opt.id
              ).length
            }));
            return { questionId: q.id, prompt: q.prompt, type: q.type, options };
          }
          if (q.type === 'short' || q.type === 'long') {
            const answers = evalList
              .map(ev => {
                if (!ev[q.id]) return null;
                return {
                  'evalTitle': evaluationDataList[ev.evalId]?.title,
                  'answer': ev[q.id]
                }
              })
              .filter(a => a !== null && a !== undefined && a !== '');
              console.log(answers);
            return { questionId: q.id, prompt: q.prompt, type: q.type, answers };
          }
          if (q.type === 'likert_scale') {
            const likertOpts = q.likertOptions || q.options || [];
            const responses = evalList
              .map(ev => ev[q.id])
              .filter(v => v !== null && v !== undefined);
            const likertResults = likertOpts.map(opt => ({
              value: opt.value,
              label: opt.label,
              count: responses.filter(v => Number(v) === opt.value).length
            }));
            const total = responses.reduce((sum, v) => sum + Number(v), 0);
            const avg = responses.length > 0 ? (total / responses.length).toFixed(1) : null;
            return { questionId: q.id, prompt: q.prompt, type: q.type, likertResults, avg, totalResponses: responses.length };
          }
          return null;
        })
        .filter(Boolean);

      return { setId: set.id, setName: set.displayName || set.name, questions };
    });

    setDisplayData(compiled);
    setCsvData(buildCsvRows(compiled));
  }

  function buildCsvRows(compiled) {
    const rows = [];
    for (const set of compiled) {
      for (const q of set.questions) {
        if (q.type === 'multiple_choice' || q.type === 'checkbox') {
          for (const opt of q.options) {
            rows.push({
              Section: set.setName,
              Question: q.prompt,
              Type: q.type,
              'Option / Answer': opt.text,
              'Evaluation': '',
              Count: opt.count,
              Average: '',
            });
          }
        } else if (q.type === 'likert_scale') {
          rows.push({
            Section: set.setName,
            Question: q.prompt,
            Type: q.type,
            'Option / Answer': '',
            'Evaluation': '',
            Count: q.totalResponses,
            Average: q.avg ?? '',
          });
        } else if (q.type === 'short' || q.type === 'long') {
          if (q.answers.length === 0) {
            rows.push({
              Section: set.setName,
              Question: q.prompt,
              Type: q.type,
              'Option / Answer': '',
              'Evaluation': '',
              Count: '',
              Average: '',
            });
          } else {
            for (const ans of q.answers) {
              rows.push({
                Section: set.setName,
                Question: q.prompt,
                Type: q.type,
                'Option / Answer': ans.answer,
                'Evaluation': ans.evalTitle,
                Count: '',
                Average: '',
              });
            }
          }
        }
      }
    }
    return rows;
  }

  if (!displayData) return null;

  return (
    <div className='data-container'>
      <div className='data-header'>
        <h4>Collected Data</h4>
        <div className='data-header-right'>
          <p>Evaluations: {Object.values(userEvaluations).length.toLocaleString()}</p>
          <CsvDownloadButton
            data={csvData}
            filename="evaluation-results"
            delimiter=","
            className='uk-button uk-button-primary uk-button-small'
          >
            Download CSV
          </CsvDownloadButton>
        </div>
      </div>
      <div className="g-space-1"></div>
      {
        displayData.map(set => (
          <div key={set.setId} className='question-set-results'>
            <h4>{set.setName}</h4>
            {
              set.questions.map(q => (
                <div key={q.questionId} className='results-container'>
                  <div className="g-space-1"></div>
                  <p className='result-title'>{q.prompt}</p>
                  {
                    (q.type === 'multiple_choice' || q.type === 'checkbox')
                      ?
                      <table className='uk-table uk-table-striped'>
                        <tbody>
                          {q.options.map(opt => (
                            <tr key={opt.id}>
                              <td>{opt.text}</td>
                              <td className='total-td' width='80px'>{opt.count.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      :
                      (q.type === 'short' || q.type === 'long')
                        ?
                        <table className='uk-table uk-table-striped'>
                          <tbody>
                            {
                              q.answers.length === 0
                                ?
                                <tr><td>No responses recorded.</td></tr>
                                :
                                q.answers.map((ans, i) => (
                                  <tr key={i}>
                                    <td>{ans.evalTitle}</td>
                                    <td>{ans.answer}</td>
                                  </tr>
                                ))
                            }
                          </tbody>
                        </table>
                        :
                        (q.type === 'likert_scale')
                          ?
                          <table className='uk-table uk-table-striped'>
                            <thead>
                              <tr>
                                {q.likertResults.map(r => (
                                  <th key={r.value} className='select-td'>
                                    {r.value} — {r.label}
                                  </th>
                                ))}
                                <th className='select-td'>Avg</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                {q.likertResults.map(r => (
                                  <td key={r.value} className='select-td'>{r.count.toLocaleString()}</td>
                                ))}
                                <td className='select-td'>{q.avg ?? 'N/A'}</td>
                              </tr>
                            </tbody>
                          </table>
                          :
                          null
                  }
                </div>
              ))
            }
          </div>
        ))
      }
    </div>
  )
}

export default EvalResultsDisplay
